import React, { useState } from "react";
import { useSelector } from "react-redux";
import { StyleSheet, Image, View } from "react-native";
import { Screen, Button, Text, InfoBox } from "../../components";
import { DropdownType } from "../../enums";
import { ThemeType } from "../../theme/theme";
import { Dropdown } from "../../components/dropdown/dropdown";
import { Stack } from "../../components/layout/stack";
import { translate } from "../../i18n/translate";
import { useTheme } from "react-native-paper";
import { useIsMounted } from "../../hooks/useIsMounted";
import { PropertyService } from "../../api/PropertyService";
import { useNavigation } from "@react-navigation/native";
import moment from "moment";
import { RootState } from "../../redux/reducers";

const DEBOUNCE_TIME = 500; //ms

export const SelectPropertyScreen = (props: any) => {
  const logger = useSelector((state: RootState) => state.logger);
  const theme = useTheme();
  const styles = getStyles(theme as ThemeType);
  const isMounted = useIsMounted();
  const navigation = useNavigation();
  const [isLoading, setLoading] = useState(false);
  const [availableProperties, setAvailableProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState<any>(null);
  const [isNoPropsFound, setIsNoPropsFound] = useState<any>(false);
  const [isSearchLoading, setSearchLoading] = useState(false);
  const [lookupTime, setLookupTime] = useState(moment().valueOf());
  const [delayedSearchTimeout, setDelayedSearchTimeout] = useState<any>();

  if (!isMounted) {
    return null;
  }

  const onPropertySelectHandler = (prop: any) => {
    const property = prop && prop.code ? { ...prop, name: prop.label, code: prop.code } : null;
    setSelectedProperty(property);
  };

  const onPropertySearchHandler = async (value: any) => {
    if (selectedProperty) setSelectedProperty(null);
    if (value.length <= 1) {
      setAvailableProperties([]);
      setIsNoPropsFound(false);
      return;
    }
    const timeValue = moment().valueOf();

    const doPropertySearch = async () => {
      setLookupTime(timeValue);
      try {
        setSearchLoading(true);
        const res = await PropertyService.searchPublicProperties(value);
        let props = res.map((prop: any) => ({ value: prop.code, ...prop }));
        props = props.filter((prop) => prop.allow_self_signup);
        setAvailableProperties(props);
        setIsNoPropsFound(!Boolean(props.length));
      } catch (error) {
        await logger.log({ error });
      }
      setSearchLoading(false);
    };

    if (timeValue - lookupTime > DEBOUNCE_TIME) {
      doPropertySearch();
    } else {
      clearTimeout(delayedSearchTimeout);
      let timeout = setTimeout(doPropertySearch, DEBOUNCE_TIME);
      setDelayedSearchTimeout(timeout);
    }
  };

  const onContinueHandler = async () => {
    setLoading(true);
    try {
      let property = await PropertyService.getPublicPropInfo(selectedProperty.code);
      navigation.navigate("SignUp", {
        ...selectedProperty,
        id: property.id,
        name: property.name,
        streetAddress: property.address_street_address,
        city: property.address_city,
        state: property.address_state,
        postalCode: property.address_postal_code,
      });
    } catch (error) {
      await logger.log({ error });
    }
    setLoading(false);
  };

  return (
    <Screen
      style={styles.container}
      preset="fixed"
      backgroundColor={theme.colors.background}
      statusBar="dark-content">
      <Stack space="tiny" style={{ zIndex: 100 }}>
        <Text style={styles.label} tx="authStack.searchAProperty" />
        <Dropdown
          onChangeText={onPropertySearchHandler}
          onSelectHandler={onPropertySelectHandler}
          value={selectedProperty}
          isLoading={isSearchLoading}
          placeholder={translate("authStack.typePropertyName")}
          items={availableProperties}
          formName="select-property"
          mode={DropdownType.DROPDOWN}
          errors={[]}
        />
      </Stack>
      {!selectedProperty && (
        <Stack space="smaller" style={styles.emptyPropContainer}>
          <Image style={styles.emptyPropIcon} source={require("../../../assets/property.png")} />
          <Text
            tx={isNoPropsFound ? "authStack.noPropertyFound" : "authStack.searchForProperty"}
            style={[styles.emptyPropText, { fontWeight: "bold" }]}
          />
          <Text
            tx={
              isNoPropsFound
                ? "authStack.confirmPropertySpelling"
                : "authStack.startTypingPropertyName"
            }
            style={styles.emptyPropText}
          />
        </Stack>
      )}
      <View style={{ zIndex: -1 }}>
        <Button
          tx="componentStatic.continue"
          onPress={onContinueHandler}
          mode="contained"
          loading={isLoading}
          style={styles.button}
          disabled={!selectedProperty}
        />
      </View>
    </Screen>
  );
};

const getStyles = (theme: ThemeType) => {
  const { colors, fontSize, spacing } = theme;
  return StyleSheet.create({
    container: {
      flexDirection: "column",
      justifyContent: "space-between",
      paddingBottom: spacing.larger,
    },
    label: {
      fontSize: fontSize.large,
      marginLeft: spacing.small,
    },
    button: { marginTop: spacing.medium, zIndex: 0 },
    emptyPropContainer: {
      zIndex: -1,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      marginTop: spacing.small,
    },
    emptyPropIcon: {
      height: theme.iconSize.huge,
      width: theme.iconSize.huge,
      resizeMode: "contain",
      marginBottom: spacing.smaller,
    },
    emptyPropText: {
      fontSize: fontSize.large,
      color: colors.darkGrey,
      textAlign: "center",
    },
  });
};
